// pages/WebsiteLinks/ListWebsiteErrors.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import DataTable from 'react-data-table-component';
import backendURL from "../../config";
import ScrapeStatusModal from "./ScrapeStatusModal";
import { Tooltip } from "@mui/material";

export default function ListWebsiteErrors({ refresh, setRefresh }) {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const fetchedUrl = queryParams.get('fetched_url');

    useEffect(() => {
        document.title = 'Website Lists | KlonIT Web Application';
        fetchDocuments();
    }, [refresh]);

    const [documents, setDocuments] = useState([]);
    const [selectedDocuments, setSelectedDocuments] = useState([]);
    const [toggleCleared, setToggleCleared] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectionError, setSelectionError] = useState(null);
    const [fetching, setFetching] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [scrapeModalIsOpen, setScrapeModalIsOpen] = useState(false);
    const [scrapeResults, setScrapeResults] = useState([]);

    const fetchDocuments = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const headers = { Authorization: `Bearer ${token}` };

            const response = await axios.get(`${backendURL}/get-unscrape-website-links`, { headers });
            const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;

            //setDocuments(response.data);
            // Filter for scrape_status === 'pending' DIRECTLY after fetching
            const pendingDocuments = responseData.filter(doc => doc.scrape_status === 'error');
            setDocuments(pendingDocuments);
            setLoading(false);
        } catch (error) {
            if (error.response && error.response.status === 401) {
                setError(error.response.data.message);
            } else {
                setError(error.message);
            }
            setLoading(false);
        }
    };

    const getFileType = (websiteLink) => {
        const lowerCaseLink = websiteLink.toLowerCase();

        if (lowerCaseLink.endsWith('.pdf')) {
            return "PDF";
        } else if (lowerCaseLink.endsWith('.doc') || lowerCaseLink.endsWith('.docx')) {
            return "Document";
        } else if (lowerCaseLink.endsWith('.png') || lowerCaseLink.endsWith('.jpg') || lowerCaseLink.endsWith('.jpeg') || lowerCaseLink.endsWith('.gif')) {
            return "Image";
        } else if (lowerCaseLink.endsWith('.csv') || lowerCaseLink.endsWith('.xlsx') || lowerCaseLink.endsWith('.xls')) {
            return "Structure Document";
        } else if (lowerCaseLink.startsWith('http') || lowerCaseLink.startsWith('https') || lowerCaseLink.startsWith('www')) {
            return "URL";
        } else {
            return "Other";
        }
    };

    const getFileTypeColor = (fileType) => {
        switch (fileType) {
            case "PDF":
                return "#8B0000";
            case "URL":
                return "#006400";
            case "Document":
                return "#A52A2A";
            case "Image":
                return "#556B2F";
            case "Structure Document":
                return "#808000";
            default:
                return "#2F4F4F";
        }
    };

    const handleRowSelected = (state) => {
        if (state.selectedRows.length <= 99) {
            setSelectedDocuments(state.selectedRows);
            setSelectionError(null);
        } else {
            setSelectionError("You can only select up to 99 rows for scraping at a time.");
            setToggleCleared(!toggleCleared);
        }
    };

    const openScrapeModal = () => setScrapeModalIsOpen(true);
    const closeScrapeModal = () => setScrapeModalIsOpen(false);

    // Delay function using Promise
    const delay = (ms) => new Promise(res => setTimeout(res, ms));

    const handleScrapeDataBTN = async () => {
        if (selectedDocuments.length === 0) {
            setError("No documents selected for scraping.");
            return;
        }

        setFetching(true);
        setError(null);
        setScrapeResults([]);
        openScrapeModal();

        const token = localStorage.getItem('access_token');
        const headers = { Authorization: `Bearer ${token}` };
        const websiteLinks_id = selectedDocuments.map(doc => doc.id);
        const websiteLinks = selectedDocuments.map(doc => doc.website_link);

        const currentScrapeResults = [];

            for (let i = 0; i < selectedDocuments.length; i++) { // Looping through selected documents (max 99 as per current limit)
                const id = websiteLinks_id[i];
                const link = websiteLinks[i];

                currentScrapeResults.push({ website_link: link, status: 'pending' });
                setScrapeResults([...currentScrapeResults]);

                console.log(`Starting scrape for URL ${i + 1}/${selectedDocuments.length}: ${link}`); // Log before request

                try {
                    const response = await axios.post(
                        `${backendURL}/add-scrape-data`,
                        { data: [[id], [link]] },
                        { headers }
                    );
                    const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;

                    if (responseData && responseData.scrape_data && responseData.scrape_data.length > 0) {
                        const scrapeData = responseData.scrape_data[0];
                        if ('page_content' in scrapeData) {
                            currentScrapeResults[i].status = 'success';
                        } else {
                            currentScrapeResults[i].status = 'error';
                            currentScrapeResults[i].error = scrapeData.error || 'Scraping failed';
                        }
                    } else if (responseData && responseData.failure_count > 0) {
                        currentScrapeResults[i].status = 'error';
                        if (responseData.failed_urls && responseData.failed_urls.length > 0) {
                           const failedUrlDetails = responseData.scrape_data.find(item => item.website_link === link);
                           currentScrapeResults[i].error = failedUrlDetails?.error || 'Scraping failed';
                        } else {
                            currentScrapeResults[i].error = 'Scraping failed';
                        }

                    } else {
                        currentScrapeResults[i].status = 'error';
                        currentScrapeResults[i].error = 'Unexpected response format';
                    }


                } catch (error) {
                    currentScrapeResults[i].status = 'error';
                    currentScrapeResults[i].error = error.message || 'Request failed';
                } finally {
                    setScrapeResults([...currentScrapeResults]);
                    if (i < selectedDocuments.length - 1) {
                        console.log(`Delaying for 3 seconds after URL ${i + 1}`); // Log before delay
                        await delay(3000); // 3 seconds delay
                        console.log(`Delay complete after URL ${i + 1}`); // Log after delay
                    }
                }
            }
        // } // End of batch loop (if batching was needed for > 99 selection)


        setFetching(false);
        setSelectedDocuments([]);
        setToggleCleared(!toggleCleared);
        setRefresh((prev) => !prev);
        closeScrapeModal();

        const failureCount = currentScrapeResults.filter(res => res.status === 'error').length;
        const successCount = currentScrapeResults.length - failureCount;

        if (failureCount > 0) {
            let errorMessage = `${successCount} website URLs were successfully scraped, while ${failureCount} failed.`;
            setError(errorMessage);
        } else {
            setError("All website URLs were successfully scraped and data is being processed.");
        }

    };


    const handleBulkDelete = async () => {
        if (selectedDocuments.length === 0) {
            setError("No documents selected for deletion.");
            return;
        }
        try {
            const token = localStorage.getItem('access_token');
            const headers = { Authorization: `Bearer ${token}` };

            await Promise.all(
                selectedDocuments.map(doc =>
                    axios.delete(`${backendURL}/delete-website-link/${doc.id}`, { headers })
                )
            );

            setDocuments(documents.filter(doc => !selectedDocuments.some(selected => selected.id === doc.id)));
            setSelectedDocuments([]);
            setToggleCleared(!toggleCleared);
            setError("Selected website links have been deleted successfully.");
        } catch (error) {
            setError(error.message);
        }
    };

    const handleDelete = async (documentId) => {
        try {
            const token = localStorage.getItem('access_token');
            const headers = { Authorization: `Bearer ${token}` };

            await axios.delete(`${backendURL}/delete-website-link/${documentId}`, { headers });
            setDocuments(documents.filter(doc => doc.id !== documentId));
            setError("Website link has been deleted successfully.");
        } catch (error) {
            setError(error.message);
        }
    };

    const handleView = (url) => {
        window.open(url, '_blank');
    };

    const columns = [
        {
            name: 'Website Link',
            selector: row => row.website_link,
            sortable: true,
            wrap: true
        },
        {
            name: 'File Type',
            selector: row => getFileType(row.website_link),
            sortable: true,
            width: '120px',
            cell: row => (
                <div style={{ backgroundColor: getFileTypeColor(getFileType(row.website_link)), color: 'white', padding: '5px', borderRadius: '3px' }}>
                    {getFileType(row.website_link)}
                </div>
            ),
        },
        {
            name: 'Status',
            selector: row => row.scrape_status,
            sortable: true,
            width: '120px',
            wrap: true
        },
        {
            name: 'Error Message',
            selector: row => row.scrape_error_message,
            sortable: true,
            width: '180px',
            wrap: true
        },
        {
            name: 'Create Date',
            selector: row => row.created_date,
            sortable: true,
            width: '150px',
        },
        {
            name: 'Action',
            cell: row => (
                <>
                    <Tooltip title="View Website Link">
                        <button className="viewIcon" onClick={() => handleView(row.website_link)}>
                            <i className="fa fa-eye fa-2x"></i>
                        </button>
                    </Tooltip>
                    <Tooltip title="Delete Website Link">
                        <button className="deleteIcon" onClick={() => handleDelete(row.id)}>
                            <i className="fa fa-trash-o fa-2x"></i>
                        </button>
                    </Tooltip>
                </>
            ),
            width: '150px',
        },
    ];

    const contextActions = (
        <div>
            <button onClick={handleScrapeDataBTN} className="btn btn-primary" disabled={fetching}>
                {fetching ? (
                    <>
                        <i className="fa fa-spinner fa-spin" aria-hidden="true"></i> Fetching Data
                    </>
                ) : (
                    "Scrape Data and Train"
                )}
            </button>
            <button onClick={handleBulkDelete} className="btn btn-danger" style={{ marginLeft: "10px" }}>
                Bulk Delete
            </button>
        </div>
    );

    const handleChange = ({ selectedRows }) => {
        const limitedSelection = selectedRows.slice(0, 99);
        setSelectedDocuments(limitedSelection);
        if (selectedRows.length > 99) {
            setSelectionError("You can only select up to 99 files at a time to scrap.");
        } else {
            setSelectionError(null);
        }
    };

    const handleSelectAllChange = (state) => {
        const { selectedCount, selectedRows } = state;
        if (selectedCount > 0) {
            const limitedSelection = documents.slice(0, 99);
            setSelectedDocuments(limitedSelection);
            setSelectionError("You can only select up to 99 files at a time to scrap.");
        } else {
            setSelectedDocuments([]);
            setSelectionError(null);
        }
        setToggleCleared(!toggleCleared);
    };

    const ExpandedRow = ({ data }) => {
        return (
            <div style={{ padding: '10px', lineHeight: '1.5' }}>
                <p><strong>Website Link:</strong> {data.website_link}</p>
            </div>
        );
    };

    const filteredDocuments = documents.filter((doc) => {
        const searchTextLower = searchText.toLowerCase();
        return (
            doc.website_link.toLowerCase().includes(searchTextLower) ||
            getFileType(doc.website_link).toLowerCase().includes(searchTextLower) ||
            (doc.is_scrape ? "Yes" : "No").toLowerCase().includes(searchTextLower) ||
            doc.created_date.toLowerCase().includes(searchTextLower)
        );
    });

    return (
        <div className="row">
            <div className="col-12 col-lg-12 col-xxl-12 d-flex">
                <div className="card flex-fill">
                    <div className="card-header"></div>
                    <div className="card-body">
                        <div className="row">
                            <div className="col-lg-8">
                                <div className="mt-1"></div>
                            </div>
                            <div className="col-lg-4">
                                <input
                                    type="text"
                                    placeholder="Search.."
                                    style={{ marginBottom: "10px", padding: "5px", width: "100%" }}
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                />
                            </div>
                        </div>

                        {fetchedUrl && (
                            <div className="alert alert-info">
                                Please select URLs to fetch the data.
                            </div>
                        )}

                        {selectedDocuments.length > 0 && selectedDocuments.length <= 99 && (
                            <div className="mt-3">
                                <div className="alert alert-info">
                                    <div className="row">
                                        <div className="col-6">
                                            {selectedDocuments.length} rows selected
                                        </div>
                                        <div className="col-6 text-end">
                                            <button
                                                onClick={handleScrapeDataBTN}
                                                className="btn btn-primary"
                                                disabled={fetching}
                                            >
                                                {fetching ? (
                                                    <>
                                                        Fetching Data{" "}
                                                        <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>
                                                    </>
                                                ) : (
                                                    "Scrape Data and Train"
                                                )}
                                            </button>
                                            <button
                                                onClick={handleBulkDelete}
                                                className="btn btn-danger"
                                                style={{ marginLeft: "10px" }}
                                            >
                                                Bulk Delete
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {selectionError && (
                            <div className="alert alert-danger mt-3">{selectionError}</div>
                        )}

                        {loading ? (
                            <p>Loading...</p>
                        ) : filteredDocuments.length === 0 ? (
                            <p>No data available</p>
                        ) : (
                            <div>
                                <DataTable
                                    columns={columns}
                                    data={filteredDocuments}
                                    pagination
                                    selectableRows
                                    selectableRowsHighlight
                                    onSelectedRowsChange={handleChange}
                                    onSelectAllRows={handleSelectAllChange}
                                    clearSelectedRows={toggleCleared}
                                    contextActions={contextActions}
                                    expandableRows
                                    expandableRowsComponent={({ data }) => (
                                        <ExpandedRow data={data} />
                                    )}
                                    expandOnRowClicked
                                />
                            </div>
                        )}
                        <br />
                        <div
                            id="getError"
                            className={`alert alert-warning ${error ? "" : "d-none"}`}
                            style={{ whiteSpace: 'pre-line' }}
                        >
                            {error}
                        </div>
                        <br />
                    </div>
                </div>
            </div>
            <ScrapeStatusModal
                isOpen={scrapeModalIsOpen}
                onRequestClose={closeScrapeModal}
                scrapeResults={scrapeResults}
            />
        </div>
    );
}